<template>
  <main id="maincontent">
    <div class="container-narrow global-component content-wrapper">
      <div class="news-list-page">
        <div class="filters-wrapper">
          <div class="filters">
            <button class="button-secondary filter-btn" @click="toggleMobileFilters">
              <span class="filter-text">Filtre</span>
              <svg class="icon icon-filter" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16"
                  fill="none">
                <path
                    d="M8.40156 2.39961L8.40156 6.39961H7.20156V4.99961L2.40156 4.99961V3.79961L7.20156 3.79961L7.20156 2.39961H8.40156ZM13.6016 3.79961V4.99961H9.60156V3.79961H13.6016ZM11.2016 5.99961V7.39961H13.6016V8.59961H11.2016V9.99961H10.0016V5.99961H11.2016ZM8.80156 7.39961V8.59961L2.40156 8.59961V7.39961L8.80156 7.39961ZM6.40156 9.59961L6.40156 13.5996L5.20156 13.5996V12.1996H2.40156V10.9996L5.20156 10.9996V9.59961H6.40156ZM13.6016 10.9996V12.1996H7.20156V10.9996H13.6016Z"
                    fill="currentColor"/>
              </svg>
            </button>

            <Dropdown
              v-model="selectedYearTags"
              :tags="yearTags.map(t => t.Value)"
              dropdownLabel="Årstal"
            />

            <Dropdown
              v-model="selectedTypeTags"
              :tags="typeTags.map(t => t.Value)"
              dropdownLabel="Type"
            />
          </div>
          <form class="search-bar" @submit.prevent="fetchData">
            <label class="search-input-wrapper">
              <input
                  type="text"
                  class="style-small"
                  placeholder="Søg..."
                  v-model="searchString"
              />
              <button type="submit" class="search-button" aria-label="Søg">
                <svg class="icon" width="18" height="18" viewBox="0 0 18 18" fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                  <path
                      d="M16.6 18L10.3 11.7C9.8 12.1 9.225 12.4167 8.575 12.65C7.925 12.8833 7.23333 13 6.5 13C4.68333 13 3.14583 12.3708 1.8875 11.1125C0.629167 9.85417 0 8.31667 0 6.5C0 4.68333 0.629167 3.14583 1.8875 1.8875C3.14583 0.629167 4.68333 0 6.5 0C8.31667 0 9.85417 0.629167 11.1125 1.8875C12.3708 3.14583 13 4.68333 13 6.5C13 7.23333 12.8833 7.925 12.65 8.575C12.4167 9.225 12.1 9.8 11.7 10.3L18 16.6L16.6 18ZM6.5 11C7.75 11 8.8125 10.5625 9.6875 9.6875C10.5625 8.8125 11 7.75 11 6.5C11 5.25 10.5625 4.1875 9.6875 3.3125C8.8125 2.4375 7.75 2 6.5 2C5.25 2 4.1875 2.4375 3.3125 3.3125C2.4375 4.1875 2 5.25 2 6.5C2 7.75 2.4375 8.8125 3.3125 9.6875C4.1875 10.5625 5.25 11 6.5 11Z"
                      fill="currentColor"/>
                  </svg>
                </button>
            </label>
          </form>

          <MobileFilters
              v-if="filters.length"
              :filters="filters"
              :is-open="mobileFiltersOpen"
              :total-visible="data?.Documents?.length || 0"
              :total-results="data?.TotalResults || 0"
              :any-filter-set="anyFilterSet"
              @close="toggleMobileFilters"
              @clear="resetSearch"
          />
      </div>

        <div v-if="data === null">
          <div class="spinner"></div>
        </div>

        <div v-else>
        <news-results :data="data"
                      :banner="banner"
                      :selected-year-tags="selectedYearTags"
                      :selected-type-tags="selectedTypeTags"
                      @fetch-more-content="fetchMoreContent"
                      @reset-search-result="resetSearch">
        </news-results>
        </div>
        </div>
    </div>
  </main>
</template>


<script setup>

import {computed, onMounted, ref, watch} from "vue";
import NewsResults from "./newsResults.vue";
import Dropdown from '../dropdown.vue';
import MobileFilters from '../mobileFilters.vue';

const data = ref(null)
const banner = ref(null)
const pageSize = ref(50)
const searchString = ref('')
const yearTags = ref([])
const typeTags = ref([])
const selectedYearTags = ref([])
const selectedTypeTags = ref([])
const mobileFiltersOpen = ref(false)

const props = defineProps({
  searchUrl: {
    type: String,
    required: true
  }
});

onMounted(async () => {
  const params = new URLSearchParams(window.location.search);
  const query = params.get('q');

  if (query) {
    searchString.value = query;
  }

  const tagMapping = {
    YearTags: selectedYearTags,
    TypeTags: selectedTypeTags,
  };

  Object.entries(tagMapping).forEach(([key, reactiveTags]) => {
    const tagValues = params.getAll(key);
    reactiveTags.value = tagValues;
  });

  await fetchData()
})

const fetchMoreContent = async () => {
  pageSize.value += 50
  await fetchData();
}

const updateURL = () => {
  const currentURL = new URL(window.location.href);
  currentURL.searchParams.set('q', searchString.value);

  const tagGroups = {
    YearTags: selectedYearTags.value,
    TypeTags: selectedTypeTags.value,
  };

  Object.entries(tagGroups).forEach(([key, tags]) => {
    currentURL.searchParams.delete(key);
    tags.forEach(tag => {
      const value = typeof tag === 'string' ? tag : tag?.Value;
      if (value) currentURL.searchParams.append(key, value);
    });
  });

  window.history.pushState(null, '', currentURL);
};

const filters = computed(() => [
  {
    label: 'Årstal',
    options: yearTags.value.map(tag => tag.Value),
    model: computed({
      get() {
        return selectedYearTags.value.map(t => t.Value);
      },
      set(newValues) {
        selectedYearTags.value = newValues.map(val => {
          return yearTags.value.find(tag => tag.Value === val) || { Value: val };
        });
      }
    })
  },
  {
    label: 'Type',
    options: typeTags.value.map(tag => tag.Value),
    model: computed({
      get() {
        return selectedTypeTags.value.map(t => t.Value);
      },
      set(newValues) {
        selectedTypeTags.value = newValues.map(val => {
          return typeTags.value.find(tag => tag.Value === val) || { Value: val };
        });
      }
    })
  }
]);

const toggleMobileFilters = () => {
  mobileFiltersOpen.value = !mobileFiltersOpen.value;
};

function closeMobileFilters() {
  mobileFiltersOpen.value = false;
}


const resetSearch = async () => {
  closeMobileFilters();

  selectedYearTags.value = []
  selectedTypeTags.value = []
  searchString.value = ''

  updateURL()

  const url = `${props.searchUrl}?q=*&maxResults=50&TypeTags=Nyhed&sort-desc=DateTags`;
  const request = await fetch(url);
  data.value = await request.json();
};

const fetchData = async () => {
  updateURL();

  const baseUrl = props.searchUrl;

  const searchQuery = searchString.value.trim() === '' ? '*' : searchString.value.trim();

  const buildTagQuery = (tags, key) =>
    tags.value
      .map(tag => `&${key}=${encodeURIComponent(tag?.Value || tag)}`)
      .join('');

  const yearTagQuery = buildTagQuery(selectedYearTags, 'YearTags');
  const typeTagQuery = buildTagQuery(selectedTypeTags, 'SubTypeTags');

  const url = `${baseUrl}?q=${searchQuery}${typeTagQuery}${yearTagQuery}&sort-desc=DateTags&maxResults=${pageSize.value}`;

  try {
    const response = await fetch(url);
    const json = await response.json();
    data.value = json;
    banner.value = json.Decorations.Banners[0] ?? null;

    if (
      (yearTags.value.length === 0 || typeTags.value.length === 0) &&
      json.GroupSummations
    ) {
      await fetchFilters(json);
    }
  } catch (err) {
    console.error('fetchData failed:', err);
  }
};

const fetchFilters = async () => {
  const url = `${props.searchUrl}?q=*`
  const request = await fetch(url);
  const result = await request.json();
  
  yearTags.value = result.GroupSummations.find(prop => prop.Property === 'YearTags').Groups;
  typeTags.value = result.GroupSummations.find(prop => prop.Property === 'SubTypeTags').Groups;
} 

watch([selectedYearTags, selectedTypeTags, searchString], () => {
  fetchData();
});

</script>